import {
    createContext,
    type ReactNode,
    useCallback,
    useMemo,
    useState,
} from "react";

import AlertModal from "../components/AlertModal";

export type AlertOptions = {
    title: string;
    description?: string;
    cancelText?: string;
    onCancel?: () => void;
};

export type AlertContextType = {
    show: (options: AlertOptions) => void;
};

export const AlertContext = createContext<AlertContextType>({
    show: () => {},
});

type Props = {
    children: ReactNode;
};

const AlertContextProvider = ({ children }: Props) => {
    const [alert, setAlert] = useState<AlertOptions>({
        title: "Alert",
        cancelText: "OK",
    });

    const [isOpen, setIsOpen] = useState(false);

    const show = useCallback(
        (alertOptions: AlertOptions) => {
            setAlert(alertOptions);
            setIsOpen(true);
        },
        [setIsOpen]
    );

    const onCancel = () => {
        alert?.onCancel?.();
        setIsOpen(false);
    };

    const value = useMemo(() => ({ show }), [show]);

    return (
        <AlertContext.Provider value={value}>
            <AlertModal
                title={alert?.title}
                description={alert?.description}
                cancelText={alert?.cancelText || "OK"}
                onCancel={onCancel}
                onClose={() => setIsOpen(false)}
                isOpen={isOpen}
            />
            {children}
        </AlertContext.Provider>
    );
};

export default AlertContextProvider;
